<template>
  <div>
        <nav
            class="
            header-navbar
            navbar navbar-expand-lg
            align-items-center
            navbar-light navbar-shadow
        ">
            <div class="navbar-container d-flex flex-wrap align-items-center justify-content-between w-100">
                <div class="bookmark-wrapper d-flex align-items-center order-1">
                    <div class="h-40">
                        <router-link :to="goToPage" class="nav-link nav-link-style d-flex p-10 btn btn-outline-primary align-items-center">
                            <i class="fas fa-calendar mb-0 mr-5"></i>
                            <span class="h5 d-none d-md-inline-block fw-bolder m-0">Online Appointment</span>
                        </router-link>
                    </div>
                </div>

                <div class="flex-grow-1 d-lg-none order-2"></div>

                <ul class="nav navbar-nav flex-row align-items-center order-3 order-lg-4">
                    <li v-if="!isLoggedIn" class="nav-item ms-1">
                        <a href="#" @click.prevent="openSignInModal" class="nav-link nav-link-style d-flex p-10 align-items-center">
                            <i class="fas fa-sign-in-alt mb-0 mr-5"></i>
                            <span class="h5 d-none d-sm-inline-block m-0">Sign In</span>
                        </a>
                    </li>
                    <li v-if="isLoggedIn" class="nav-item dropdown dropdown-user ms-1">
                        <a
                            class="nav-link dropdown-toggle dropdown-user-link"
                            id="dropdown-user"
                            href="#"
                            data-bs-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                        >
                            <div class="user-nav d-sm-flex d-none">
                                <span class="user-name fw-bolder">{{ user.name }}</span>
                            </div>
                            <span class="avatar"
                            ><img
                                class="round"
                                :src="profile.photo"
                                alt="avatar"
                                height="40"
                                width="40" /><span class="avatar-status-online"></span>
                            </span>
                        </a>
                        <div
                            class="dropdown-menu dropdown-menu-end"
                            aria-labelledby="dropdown-user"
                        >
                            <router-link :to="{'name': 'online-appointment'}" class="dropdown-item">
                                <a> <img class="mr-10" width="14" src="/app-assets/images/icons/user.svg" alt=""> My Profile </a>
                            </router-link>
                            <a @click.prevent="logout" class="dropdown-item">
                                <img class="mr-10" width="14" src="/app-assets/images/icons/power.svg" alt=""> Logout
                            </a>
                        </div>
                    </li>
                </ul>

                <div class="hospital-nav-container w-100 w-lg-auto flex-grow-lg-1 d-flex justify-content-lg-center mx-lg-2 mt-1 mt-lg-0 order-4 order-lg-2">
                    <div class="selector-wrapper w-100">
                        <AsyncSelect
                            placeholder="Select Hospital"
                            v-model="company"
                            :api-service="fetchCompanies"
                            label="name"
                            :disabled="isDisabled"
                            :is-clearable="false"
                        />
                    </div>
                </div>
            </div>
        </nav>

        <slot />

        <PatientSignInModal
            :is-open="showSignInModal"
            @close="showSignInModal = false"
        />
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import handleProfile from "@/services/modules/profile";
import handleOnlineAppointment from "@/services/modules/online-appointment/onlineAppointment";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import { useRoute } from "vue-router";
import PatientSignInModal from "@/components/molecule/online-appointment/PatientSignInModal.vue";

const store = useStore();
const router = useRouter();
const route = useRoute();
const showError = inject('showError');
const { fetchProfileInfo } = handleProfile();
const { fetchDoctorAppointmentInfo, fetchCompanies } = handleOnlineAppointment();

const loading = ref(false);
const company = ref(null);
const appointmentInfo = ref(null);
const showSignInModal = ref(false);

const user = computed(() => store.state.user.user);
const profile = computed(() => store.state.user.profile);
const isLoggedIn = computed(() => !!user.value && !!user.value.id);
const isDisabled = computed(() => appointmentInfo.value && appointmentInfo.value.total_companies <= 1);

const openSignInModal = () => {
    showSignInModal.value = true;
};

const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("X-Public-Token");
    router.push('/login');
};

const setUser = () => store.commit('user/setUser');
const setProfile = (data) => store.commit('user/setProfile', data);

const goToPage = computed(() => {
    const item = { 
        name: 'online-appointment',
        query: {}
    };
    if (route.query.companyId) {
        item.query.companyId = route.query.companyId;
    }
    return item;
});

const getProfile = async () => {
    loading.value = true;
    try {
        const res = await fetchProfileInfo();
        if (res.status) {
            setProfile(res.data);
        } else {
            showError(res.message);
        }
    } catch (e) {
        console.error(e);
    } finally {
        loading.value = false;
    }
};

const getDoctorAppointmentInfo = async () => {

    let query = `?company_id=${route.query.companyId || ''}`;

    loading.value = true;
    try {
        const res = await fetchDoctorAppointmentInfo(query);
        if (!res.status) {
            showError(res.message);
            return;
        }

        appointmentInfo.value = res.data;
        company.value = appointmentInfo.value.company;
    } catch (e) {
        showError('Something went wrong during fetching doctor appointment info');
    } finally {
        loading.value = false;
    }
}

setUser();

getDoctorAppointmentInfo();

watch(() => company.value, (newValue) => {
    if (! newValue) return;
    store.commit('dental/setCompany', newValue);
    router.push({
        name: route.name, 
        query: {companyId: newValue.id}
    });
}, {immediate: true})

onMounted(() => {
    if (isLoggedIn.value) {
        getProfile();
    }
});
</script>

<style scoped>
.hospital-nav-container {
    width: 100%;
}
.selector-wrapper {
    max-width: none;
}

@media (min-width: 992px) {
    .hospital-nav-container {
        width: auto;
        max-width: 500px;
    }
    .selector-wrapper {
        max-width: 400px;
    }
}

@media (max-width: 991px) {
    .navbar-container {
        padding: 0 10px;
    }
}

.mr-10 {
    margin-right: 10px;
}
</style>
